import React from 'react';
import Swal from 'sweetalert2';
import membersForms from 'src/services/api/membersForms';
import { chPerm } from 'src/components/helpers';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';
import {
  getNewMemberCategoryInfo,
  hasMixedGroupOption,
  isCoupleCivilStatus,
} from 'src/containers/adminDashboard/utils/groupsTranslationMap';
import { Page, FormCard } from 'src/containers/conexion/styles';
import { validateNewMembersForm } from 'src/containers/conexion/utils/formValidation';
import {
  buildBaseGroupInputs,
  buildGroupInputsWithActivity,
  buildExistingGroupInputs,
  resolveActivityName,
} from 'src/containers/conexion/utils/groupInputs';
import GroupPreferenceStep from 'src/containers/conexion/components/GroupPreferenceStep';
import GroupSelectionStep from 'src/containers/conexion/components/GroupSelectionStep';
import GroupSummaryStep from 'src/containers/conexion/components/GroupSummaryStep';
import NewMembersPersonalForm from 'src/containers/conexion/components/NewMembersPersonalForm';
import PreloginStep from 'src/containers/conexion/components/PreloginStep';
import SuccessView from 'src/containers/conexion/components/SuccessView';
import {
  attendantFromNewMembersLookup,
  baseAttendantFromInputs,
  fetchNewMembersLookup,
} from 'src/containers/conexion/utils/newMembersLookup';

const CATEGORY_ERROR =
  'No pudimos determinar tu categoría de grupo. Verifica género, fecha de nacimiento y estado civil.';

const NewMembersForm = () => {
  const [Attendant, set_Attendant] = React.useState({});
  const [errorInputs, set_errorInputs] = React.useState({});
  const [step, setStep] = React.useState('prelogin');
  const [sending, set_sending] = React.useState(false);
  const [groupInputs, setGroupInputs] = React.useState({});
  const [groupPreference, setGroupPreference] = React.useState('');
  const [preferenceError, setPreferenceError] = React.useState(false);
  const getCaptchaToken = useRecaptchaToken('new_members');
  const getLookupCaptchaToken = useRecaptchaToken('new_members_lookup');

  const virtualFill = window.location.href.includes('casa');
  const captchaRequired = isCaptchaRequired() && !chPerm(['newMembersFill', 'newMembers']);
  const hasErrors = Object.values(errorInputs).includes(true);
  const alreadySubmitted = Boolean(Attendant.alreadySubmitted);

  const categoryInfo = getNewMemberCategoryInfo({
    gender: Attendant.gender,
    birthdate: Attendant.birthdate,
    civil: Attendant.civil,
  });

  const includeCouplesOption = isCoupleCivilStatus(Attendant.civil);
  const canChooseGroupType = hasMixedGroupOption({
    gender: Attendant.gender,
    birthdate: Attendant.birthdate,
    civil: Attendant.civil,
  });

  const resetFlow = () => {
    setStep('prelogin');
    set_Attendant({});
    set_errorInputs({});
    setGroupInputs({});
    setGroupPreference('');
    setPreferenceError(false);
  };

  const lookupNewMember = async ({ identification, phone } = {}) => {
    const captcha = await getLookupCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return null;
    }

    try {
      const response = await fetchNewMembersLookup({
        identification,
        phone,
        captcha,
        captchaRequired,
      });
      const data = response.data || {};
      if (!data.success) {
        Swal.fire(data.message || 'Hubo un error, por favor intenta más tarde.');
        return null;
      }

      const mapped = attendantFromNewMembersLookup(data, { identification, phone });
      if (mapped) {
        set_Attendant((prev) => ({ ...prev, ...mapped, identification, phone }));
      }

      return {
        data,
        mapped,
        attendant: mapped || baseAttendantFromInputs({ identification, phone }),
      };
    } catch (error) {
      Swal.fire(error.response?.data?.message || 'Hubo un error, por favor intenta más tarde.');
      return null;
    }
  };

  const onPreloginContinue = (preloaded) => {
    set_Attendant(preloaded);
    set_errorInputs({});

    if (preloaded.alreadyInGroup && preloaded.groupMember) {
      setGroupInputs(
        buildExistingGroupInputs({
          identification: preloaded.identification,
          phone: preloaded.phone,
          groupMember: preloaded.groupMember,
          Attendants: preloaded.Attendants,
          guest: preloaded,
        })
      );
      setStep('summary');
      return;
    }

    setStep('form');
  };

  const verifyForm = () => {
    const nextErrorInputs = validateNewMembersForm(Attendant, { alreadySubmitted });
    set_errorInputs(nextErrorInputs);
    return Object.values(nextErrorInputs).includes(true);
  };

  const goToGroupSelection = (preference) => {
    const nextGroupInputs = buildGroupInputsWithActivity(Attendant, preference);
    if (!nextGroupInputs) {
      Swal.fire(CATEGORY_ERROR);
      return;
    }
    setGroupInputs(nextGroupInputs);
    setStep('selectGroup');
  };

  const continueToGroupFlow = () => {
    if (!resolveActivityName(Attendant, 'gender') && !includeCouplesOption) {
      Swal.fire(CATEGORY_ERROR);
      return;
    }

    setGroupInputs(buildBaseGroupInputs(Attendant));
    if (canChooseGroupType) {
      setGroupPreference('');
      setPreferenceError(false);
      setStep('groupPreference');
    } else {
      goToGroupSelection('gender');
    }
  };

  const sendForm = async (e) => {
    e.preventDefault();
    if (verifyForm()) return;

    if (alreadySubmitted) {
      continueToGroupFlow();
      return;
    }

    const wantsGroup = Attendant.interestedGroup === 'Sí';

    if (wantsGroup && !resolveActivityName(Attendant, 'gender') && !includeCouplesOption) {
      Swal.fire(CATEGORY_ERROR);
      return;
    }

    set_sending(true);
    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      set_sending(false);
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    const payload = {
      ...Attendant,
      virtualFill,
      ...(captchaRequired ? { captcha } : {}),
    };

    membersForms
      .newMembers(payload)
      .then((response) => {
        set_sending(false);
        if (response.data.success) {
          if (wantsGroup) {
            continueToGroupFlow();
          } else {
            setStep('done');
            Swal.fire('¡Se ha enviado correctamente!');
          }
        } else if (response.data.code === 'ALREADY_SUBMITTED_WITHIN_6_MONTHS') {
          set_Attendant((prev) => ({
            ...prev,
            alreadySubmitted: true,
            submittedAt: response.data.submittedAt,
            interestedGroup: 'Sí',
          }));
          continueToGroupFlow();
        } else {
          Swal.fire(response.data.message || 'Hubo un error, por favor intenta más tarde.');
        }
      })
      .catch(() => {
        set_sending(false);
        Swal.fire('Hubo un error, por favor intenta más tarde.');
      });
  };

  const continueFromPreference = () => {
    if (!groupPreference) {
      setPreferenceError(true);
      return;
    }
    setPreferenceError(false);
    goToGroupSelection(groupPreference);
  };

  const handleInputs = (event) => {
    const { name, value } = event.target;
    set_Attendant({ ...Attendant, [name]: value });
    set_errorInputs((prev) => ({ ...prev, [name]: false }));
  };

  const selectHandle = (a, { value, name }) => {
    handleInputs({ target: { value, name } });
  };

  const setOption = (name, value) => {
    set_Attendant({ ...Attendant, [name]: value });
    set_errorInputs((prev) => ({ ...prev, [name]: false }));
  };

  const toggleHabeas = () => {
    const next = !Attendant.habeasDataAccepted;
    set_Attendant({ ...Attendant, habeasDataAccepted: next });
    set_errorInputs((prev) => ({ ...prev, habeasDataAccepted: false }));
  };

  const onSelectPreference = (value) => {
    setGroupPreference(value);
    setPreferenceError(false);
  };

  if (step === 'prelogin') {
    return (
      <Page>
        <PreloginStep onContinue={onPreloginContinue} lookupNewMember={lookupNewMember} />
      </Page>
    );
  }

  if (step === 'groupPreference') {
    return (
      <Page>
        <GroupPreferenceStep
          gender={Attendant.gender}
          includeCouples={includeCouplesOption}
          categoryInfo={categoryInfo}
          groupPreference={groupPreference}
          preferenceError={preferenceError}
          onSelectPreference={onSelectPreference}
          onContinue={continueFromPreference}
          onBack={() => setStep('form')}
        />
      </Page>
    );
  }

  if (step === 'selectGroup') {
    return (
      <Page>
        <GroupSelectionStep
          groupInputs={groupInputs}
          setGroupInputs={setGroupInputs}
          setStep={setStep}
          backStep={canChooseGroupType ? 'groupPreference' : 'form'}
        />
      </Page>
    );
  }

  if (step === 'summary') {
    return (
      <Page>
        <GroupSummaryStep groupInputs={groupInputs} onRestart={resetFlow} />
      </Page>
    );
  }

  return (
    <Page>
      <FormCard className="Registration__form" onSubmit={sendForm}>
        {step === 'done' ? (
          <SuccessView name={Attendant.name} virtualFill={virtualFill} />
        ) : (
          <NewMembersPersonalForm
            Attendant={Attendant}
            categoryInfo={categoryInfo}
            errorInputs={errorInputs}
            hasErrors={hasErrors}
            sending={sending}
            handleInputs={handleInputs}
            selectHandle={selectHandle}
            setOption={setOption}
            toggleHabeas={toggleHabeas}
            setAttendant={set_Attendant}
            setErrorInputs={set_errorInputs}
            onGuestUpdated={lookupNewMember}
          />
        )}
      </FormCard>
    </Page>
  );
};

export default NewMembersForm;
